import {
  barSettings,
  freqCount,
  normalizedEQGainLookUp,
  normalizedEQFreqLookup,
  defaultNormalizedEQGain,
} from "./constants";
import {
  allowedFreqAndGainToNormalizedFreqAndGain,
  coordsToAllowedFreqAndGain,
  generateCoordsFromNormalized,
} from "./converters";
import { curveBumpX, drag, line, pointer, pointers, range } from "d3";
import { GraphDrawingParams } from "./EqualizerGraph";
import classNames from "classnames";

function roundToFreqAndGainCenters(p: number, n: number, size: number) {
  const a = p - (p % n);
  const b = p + n - (p % n);

  if (a >= size) {
    return b;
  }

  if (b >= size) {
    return a;
  }

  return p % n < n / 2 ? a : b;
}

function getSnappedToGridCoordsFromEvent(
  xFromEvent: number,
  yFRomEvent: number,
  { freqResolution, width, gainResolution, height }: GraphDrawingParams
) {
  const realX =
    xFromEvent > width ? width - 1 : xFromEvent < 0 ? 0 : xFromEvent;
  const realY =
    yFRomEvent > height ? height - 1 : yFRomEvent < 0 ? 0 : yFRomEvent;

  const x =
    freqResolution / 2 +
    roundToFreqAndGainCenters(
      Math.min(width, realX - freqResolution / 2),
      freqResolution,
      width
    );

  const y =
    gainResolution / 2 +
    roundToFreqAndGainCenters(
      Math.min(height, realY - gainResolution / 2),
      gainResolution,
      height
    );

  return { x, y };
}

export function drawGraphBars({
  svg,
  width,
  height,
  freqCount,
}: GraphDrawingParams) {
  const data = range(freqCount);
  const barSelector = "freqBarRect";
  const textSelector = "eqBandText";
  const freqSelector = "eqBandFreqText";
  const barPadding = Math.floor(width / 80);
  const barWidth = width / freqCount;

  svg
    .selectAll(`.${barSelector}`)
    .data(data)
    .join("rect")
    .attr("class", (d) => `${barSelector} ${barSettings.bars[d].barClassName}`)
    .attr("x", (d) => (width / freqCount) * d)
    .attr("width", barWidth)
    .attr("height", height);

  svg
    .selectAll(`.${textSelector}`)
    .data(data)
    .join("text")
    .attr(
      "class",
      (d) => `${textSelector} select-none ${barSettings.bars[d].textClassName}`
    )
    .attr(
      "style",
      `font-size: ${Math.min(Math.floor(height / 8))}px; text-anchor: start;`
    )
    .text((d) => barSettings.bars[d].text)
    .attr("y", (d) => -(width / freqCount) * d - barPadding)
    .attr("x", barPadding)
    .attr("transform", "rotate(90)");

  svg
    .selectAll(`.${freqSelector}`)
    .data(data)
    .join("text")
    .attr(
      "class",
      (d) => `${freqSelector} ${barSettings.bars[d].textClassName} select-none`
    )
    .attr(
      "style",
      `font-size: ${Math.min(
        Math.floor(width / 25),
        16
      )}px; text-anchor: middle`
    )
    .text((d) => barSettings.bars[d].freq)
    .attr("x", (d) => (width / freqCount) * d + barWidth / 2)
    .attr("y", height - Math.floor(height / 35));
}

export function drawGraphLines({
  height,
  svg,
  width,
  freqResolution,
  gainResolution,
  gainCount,
}: GraphDrawingParams) {
  const verticalLineSelector = "verticalLine";
  const horizLinesSelector = "horizLines";
  const gainTextSelector = "gainText";

  // the lines where the circle will stick to - center of the bar graphs
  svg
    .selectAll(`.${verticalLineSelector}`)
    .data(range(freqCount))
    .join("line")
    .attr(
      "class",
      (d) =>
        `${verticalLineSelector} ${barSettings.bars[d].barSplitLineClassName} stroke-1`
    )
    .attr("style", "stroke-dasharray: 2, 2;")
    .attr("x1", (d) => freqResolution / 2 + d * freqResolution)
    .attr("y1", 0)
    .attr("x2", (d) => freqResolution / 2 + d * freqResolution)
    .attr("y2", height - Math.round(height / 11.5));

  // the eq gain lines
  svg
    .selectAll(`.${horizLinesSelector}`)
    .data(range(gainCount))
    .join("line")
    .attr("class", (d) =>
      classNames("stroke-1", horizLinesSelector, "stroke-purple-400")
    )
    .attr("x1", (d) => {
      // stop lines before the word 'bass'
      if (d === 0) {
        return `${Math.floor(width / 15)}px`;
      }

      return 0;
    })
    .attr("y1", (d) => gainResolution / 2 + d * gainResolution)
    .attr("x2", (d) => width)
    .attr("y2", (d) => gainResolution / 2 + d * gainResolution);

  // the eq gain text
  svg
    .selectAll(`.${gainTextSelector}`)
    .data(range(gainCount))
    .join("text")
    .attr(
      "class",
      `${gainTextSelector} ${barSettings.bars[3].textClassName} select-none`
    )
    .attr(
      "style",
      `font-size: ${Math.min(Math.floor(width / 30), 12)}px; text-anchor: end;`
    )
    .text((d) => {
      const number = normalizedEQGainLookUp.get(d) || 0;

      if (number >= 0) {
        return `+${number} db`;
      }

      return `${number} db`;
    })
    .attr("y", (d) => gainResolution / 2 + d * gainResolution - 4)
    .attr("x", width - 4);
}

export function drawCircle(graphProps: GraphDrawingParams) {
  const {
    svg,
    value: internalValue,
    gainResolution,
    freqResolution,
    circleRadius,
    setValue: setInternalValue,
  } = graphProps;
  const normalizedValues =
    allowedFreqAndGainToNormalizedFreqAndGain(internalValue);
  const getCoords = generateCoordsFromNormalized(graphProps);
  const innerCircleSelector = "innerCircle";
  const outerCircleSelector = "outerCircle";

  const circles = svg
    .selectAll(`.${outerCircleSelector}`)
    .data([normalizedValues])
    .join("circle")
    .attr(
      "class",
      `${outerCircleSelector} transition-transform fill-purple-200 opacity-80 drop-shadow`
    )
    .attr("style", "transform-box: fill-box; transform-origin: center center")
    .attr("cx", (d) => getCoords.x(d.freq))
    .attr("cy", (d) => getCoords.y(d.gain))
    .attr("r", 60);

  const innerCircle = svg
    .selectAll(`.${innerCircleSelector}`)
    .data([normalizedValues])
    .join("circle")
    .attr(
      "class",
      `${innerCircleSelector} transition-transform fill-purple-100 drop-shadow-md`
    )
    .attr("style", "transform-box: fill-box; transform-origin: center center")
    .attr("cx", (d) => getCoords.x(d.freq))
    .attr("cy", (d) => getCoords.y(d.gain))
    .attr("r", circleRadius);

  const dragged = drag<SVGCircleElement, { gain: number; freq: number }>()
    .on("start", function (event, d) {
      circles.raise().attr("data-active", true);
      innerCircle.raise().attr("data-active", true);
    })
    .on("end", function () {
      circles.raise().attr("data-active", false);
      innerCircle.raise().attr("data-active", false);
    })
    .on("drag", function (event, ...args) {
      if (window.TouchEvent && event.sourceEvent instanceof TouchEvent) {
        return;
      }

      const [x, y] = pointer(event, this);

      setInternalValue?.(
        coordsToAllowedFreqAndGain({
          freqResolution,
          gainResolution,
          ...getSnappedToGridCoordsFromEvent(x, y, graphProps),
        })
      );
    });

  // Mobile version to allow drag
  [circles, innerCircle].forEach((circle, i) => {
    circle.on(
      "touchmove",
      function (event) {
        if (event.sourceEvent instanceof MouseEvent) {
          return;
        }

        event.preventDefault();
        const [x, y] = pointers(event, this)[0];

        setInternalValue?.(
          coordsToAllowedFreqAndGain({
            freqResolution,
            gainResolution,
            ...getSnappedToGridCoordsFromEvent(x, y, graphProps),
          })
        );
      },
      { passive: true }
    );
  });

  circles.call(dragged as any);
  innerCircle.call(dragged as any);
}

export function drawGraphLine(graphProps: GraphDrawingParams) {
  const { svg, value: internalValue, width } = graphProps;
  const lineCurve = line().curve(curveBumpX);

  function getEQLinePoints(): [number, number][] {
    const normalizedValues =
      allowedFreqAndGainToNormalizedFreqAndGain(internalValue);
    const getCoords = generateCoordsFromNormalized(graphProps);

    const points: [number, number][] = Array.from(normalizedEQFreqLookup).map(
      ([freqValue]) => {
        const gain =
          normalizedValues.freq === freqValue
            ? normalizedValues.gain
            : defaultNormalizedEQGain;

        return [getCoords.x(freqValue), getCoords.y(gain)];
      }
    );

    const head: [number, number] = [0, points[0][1]];
    const tail: [number, number] = [width, points[points.length - 1][1]];

    return [head, ...points, tail];
  }

  const eqLineSelector = "eqLine";

  svg
    .selectAll(`.${eqLineSelector}`)
    .data([getEQLinePoints()])
    .join("path")
    .attr("class", `${eqLineSelector} stroke-purple-100 fill-none stroke-2`)
    .attr("d", (d) => lineCurve(d));
}

export function handleClick(graphProps: GraphDrawingParams) {
  const {
    svg,
    setValue: setInternalValue,
    freqResolution,
    gainResolution,
  } = graphProps;
  svg.on("click", function (event) {
    const [x, y] = pointer(event, this);

    setInternalValue?.(
      coordsToAllowedFreqAndGain({
        freqResolution,
        gainResolution,
        ...getSnappedToGridCoordsFromEvent(x, y, graphProps),
      })
    );
  });
}
